/** FuryBot data service — firm loading, caching, structured answer objects */
import axios from "axios";
import { BOT_API } from "../constants";
import { getAffiliateLink } from "@/utils/tracking";
import { findByNumericSize, numToSizeLabel } from "@/utils/sizeUtils";
import { parsePrice } from "@/utils/priceUtils";
import { formatFirmDisplay } from "@/utils/formatFirmDisplay";
import { computeFFScore } from "@/utils/furyScore";

export async function fetchFirms(firmsRef, setFirms, buildAlias) {
  if (firmsRef.current.length > 0) return firmsRef.current;
  try {
    const r = await axios.get(`${BOT_API}/firms`);
    firmsRef.current = r.data || [];
    setFirms(r.data || []);
    if (buildAlias) buildAlias(r.data || []);
    sessionStorage.setItem("furybot_firms", JSON.stringify(r.data || []));
    return r.data || [];
  } catch { return []; }
}

export function loadCachedFirms(firmsRef, setFirms, buildAlias) {
  const cached = sessionStorage.getItem("furybot_firms");
  if (cached) {
    try {
      const d = JSON.parse(cached);
      firmsRef.current = d;
      setFirms(d);
      if (buildAlias) buildAlias(d);
    } catch {}
  }
}

export const createFirmCard = (firm, matchReason = null, promos = [], contextLine = null) => {
  const p50 = findByNumericSize(firm.pricing, 50000);
  const fd = formatFirmDisplay(firm, p50);
  const promo = promos.find(p => p.firm_name === firm.name && p.is_active);
  const ffScore = computeFFScore(firm);
  return {
    type: "firm_card",
    firm_name: firm.name,
    slug: firm.slug,
    eval_price: fd.pricePrimary,
    fury_score: ffScore.total,
    discount_pct: fd.discountValid ? fd.discountPercent : 0,
    discount_code: fd.couponCode,
    drawdown: fd.drawdownShort,
    profit_split: fd.profitSplit,
    payout: fd.payoutSchedule,
    match_reason: matchReason,
    context_line: contextLine,
    rating: firm.verified_trader_rating || null,
    review_count: firm.verified_trader_rating_count || null,
    is_discount_live: firm.is_discount_live || fd.discountValid || false,
    affiliate_url: getAffiliateLink(firm),
    profile_url: `/firms/${firm.slug}`,
    discount_url: `/${firm.slug}-discount`,
    flash_deal: promo ? { code: promo.code, percentage: promo.percentage, conditions: promo.conditions, expiry: promo.expiry_date } : null,
  };
};

export const createDealCard = (firm, promos = [], targetSize = 50000) => {
  const pricingEntry = findByNumericSize(firm.pricing, targetSize);
  const fd = formatFirmDisplay(firm, pricingEntry);
  const flash = promos.filter(p => p.firm_name === firm.name && p.is_active);
  return {
    type: "deal_card",
    firm_name: firm.name,
    slug: firm.slug,
    pricePrimary: fd.pricePrimary,
    priceOriginal: fd.priceOriginal,
    savingsDisplay: fd.savingsDisplay,
    discountDisplay: fd.discountDisplay,
    discountValid: fd.discountValid,
    couponCode: fd.couponCode,
    standard_discount: fd.discountValid ? { pct: fd.discountPercent, code: fd.couponCode } : null,
    flash_deals: flash.map(p => ({ code: p.code, percentage: p.percentage, conditions: p.conditions, expiry: p.expiry_date, source: p.source_tweet_url })),
    affiliate_url: getAffiliateLink(firm),
    discount_url: `/${firm.slug}-discount`,
    sizeLabel: numToSizeLabel(targetSize),
  };
};

export function matchFirms(firms, prefs, promos) {
  return firms.map(f => {
    let score = 0;
    const reasons = [];
    const fd = formatFirmDisplay(f);
    const p50 = findByNumericSize(f.pricing, 50000);
    const price = parsePrice(p50?.price);
    if (prefs.budget === "under100") { if (price <= 100) score += 3; else if (price <= 150) score += 1; reasons.push(price <= 100 ? "Fits your budget" : "Close to budget"); }
    else if (prefs.budget === "100-200") { if (price <= 200) score += 2; else score += 1; if (price <= 200) reasons.push("Within budget"); }
    else if (prefs.budget === "200-400") { if (price <= 400) score += 2; }
    else { score += 1; }
    if (prefs.style === "scalping") {
      if (!f.min_hold_seconds || f.min_hold_seconds === 0) { score += 2; reasons.push("No min hold time — scalp-friendly"); }
      if (["trailing_eod", "static", "hybrid"].includes(f.drawdown_type)) { score += 2; reasons.push("Forgiving drawdown for scalpers"); }
    }
    if (prefs.style === "swing" && ["trailing_eod", "static"].includes(f.drawdown_type)) { score += 2; reasons.push("EOD/static drawdown works for swing"); }
    if (prefs.style === "day" || prefs.style === "mix") { score += 1; }
    if (prefs.experience === "first" && price <= 150) { score += 2; reasons.push("Affordable for first eval"); }
    if (prefs.experience === "experienced") { score += 1; }
    if (prefs.priority === "cheapest" && price <= 100) { score += 3; reasons.push("One of the cheapest evals"); }
    if (prefs.priority === "drawdown") { if (["trailing_eod", "static", "hybrid"].includes(f.drawdown_type)) { score += 3; reasons.push(`${fd.drawdownShort} drawdown`); } }
    if (prefs.priority === "split" && f.profit_split?.includes("100")) { score += 3; reasons.push("High profit split"); }
    if (prefs.priority === "payout") { score += 1; if (f.payout_frequency?.toLowerCase().includes("week")) { score += 2; reasons.push("Weekly payouts"); } }
    if (prefs.comfort === "forgiving" && ["static", "trailing_eod", "hybrid"].includes(f.drawdown_type)) { score += 2; }
    if (fd.discountValid && fd.discountPercent > 20) { score += 1; reasons.push(`${fd.discountPercent}% OFF active`); }
    // Light Fury Score augmentation: firms with higher overall quality get a tiebreaker boost
    const ffScore = computeFFScore(f);
    score += ffScore.total >= 8 ? 1 : ffScore.total >= 7 ? 0.5 : 0;
    return { firm: f, score, reasons: reasons.slice(0, 3), promos };
  }).sort((a, b) => b.score - a.score).slice(0, 3);
}

export function getWhyBullets(prefs) {
  const bullets = [];
  if (prefs.budget === "under100") bullets.push("Lower evaluation cost (under $100)");
  if (prefs.budget === "100-200") bullets.push("Moderate evaluation pricing ($100-200)");
  if (prefs.style === "scalping") bullets.push("Scalp-friendly rules (no hold time, forgiving drawdown)");
  if (prefs.style === "swing") bullets.push("Swing-friendly drawdown (EOD or static)");
  if (prefs.style === "day") bullets.push("Day trading compatible evaluation rules");
  if (prefs.experience === "first") bullets.push("Beginner-friendly with clear, simple rules");
  if (prefs.priority === "cheapest") bullets.push("Lowest total cost to get funded");
  if (prefs.priority === "drawdown") bullets.push("Forgiving drawdown that doesn't punish normal pullbacks");
  if (prefs.priority === "split") bullets.push("High profit split (90-100%)");
  if (prefs.priority === "payout") bullets.push("Fast payout cycles (weekly or daily)");
  return bullets.slice(0, 3);
}
